import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { RefreshCw, Check, Calendar, Sheet, Link2, Unlink, Mail, Settings, Key, Eye, EyeOff, Zap, Plus, Trash2, Edit2, X, Copy, Shield, ChevronDown, Rocket } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cores disponíveis para procedimentos
const CORES_DISPONIVEIS = [
  { nome: 'Roxo', cor: '#8B5CF6' },
  { nome: 'Rosa', cor: '#EC4899' },
  { nome: 'Azul', cor: '#3B82F6' },
  { nome: 'Verde', cor: '#10B981' },
  { nome: 'Laranja', cor: '#F97316' },
  { nome: 'Vermelho', cor: '#EF4444' },
  { nome: 'Amarelo', cor: '#EAB308' },
  { nome: 'Ciano', cor: '#06B6D4' },
  { nome: 'Índigo', cor: '#6366F1' },
  { nome: 'Lime', cor: '#84CC16' },
];

function ConfiguracoesPage({ user, onLogout, showConfig }) {
  const navigate = useNavigate();
  const [googleStatus, setGoogleStatus] = useState({ 
    connected: false, 
    selected_calendar_id: '',
    google_email: '',
    has_credentials: false,
    client_id: ''
  });
  const [syncStatus, setSyncStatus] = useState({
    auto_sync_enabled: false,
    sync_interval_seconds: 5
  });
  const [calendars, setCalendars] = useState([]);
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [syncing, setSyncing] = useState({ calendar: false, sheets: false });
  const [selectingCalendar, setSelectingCalendar] = useState(null);
  const [showCalendarSelector, setShowCalendarSelector] = useState(false);
  
  // Credentials state
  const [showCredentialsForm, setShowCredentialsForm] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [savingCredentials, setSavingCredentials] = useState(false);

  // Procedimentos state
  const [procedimentos, setProcedimentos] = useState([]);
  const [loadingProcedimentos, setLoadingProcedimentos] = useState(true);
  const [showProcedimentoForm, setShowProcedimentoForm] = useState(false);
  const [editingProcedimento, setEditingProcedimento] = useState(null);
  const [novoProcedimento, setNovoProcedimento] = useState({ nome: '', cor: '#8B5CF6', valor_padrao: '' });
  const [savingProcedimento, setSavingProcedimento] = useState(false);

  // API Key state
  const [apiKey, setApiKey] = useState(null);
  const [apiKeyCreatedAt, setApiKeyCreatedAt] = useState(null);
  const [loadingApiKey, setLoadingApiKey] = useState(false);
  const [generatingApiKey, setGeneratingApiKey] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [copiedApiKey, setCopiedApiKey] = useState(false);
  const [expandedEndpoint, setExpandedEndpoint] = useState('retornos');
  const [copiedCurl, setCopiedCurl] = useState(false);

  useEffect(() => {
    checkGoogleStatus();
    checkSyncStatus();
    loadProcedimentos();
    loadApiKey();
  }, []);

  const loadProcedimentos = async () => {
    setLoadingProcedimentos(true);
    try {
      const response = await axios.get(`${API}/procedimentos`);
      setProcedimentos(response.data);
    } catch (error) {
      console.error('Error loading procedimentos:', error);
      toast.error('Erro ao carregar procedimentos');
    } finally {
      setLoadingProcedimentos(false);
    }
  };

  const handleSaveProcedimento = async () => {
    if (!novoProcedimento.nome.trim()) {
      toast.error('Informe o nome do procedimento');
      return;
    }
    
    setSavingProcedimento(true);
    try {
      const data = {
        nome: novoProcedimento.nome.trim(),
        cor: novoProcedimento.cor,
        valor_padrao: novoProcedimento.valor_padrao ? parseFloat(novoProcedimento.valor_padrao) : null
      };
      
      if (editingProcedimento) {
        await axios.put(`${API}/procedimentos/${editingProcedimento.id}`, data);
        toast.success('Procedimento atualizado!');
      } else {
        await axios.post(`${API}/procedimentos`, data);
        toast.success('Procedimento criado!');
      }
      
      setShowProcedimentoForm(false);
      setEditingProcedimento(null);
      setNovoProcedimento({ nome: '', cor: '#8B5CF6', valor_padrao: '' });
      loadProcedimentos();
    } catch (error) {
      console.error('Error saving procedimento:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar procedimento');
    } finally {
      setSavingProcedimento(false);
    }
  };

  const handleEditProcedimento = (proc) => {
    setEditingProcedimento(proc);
    setNovoProcedimento({
      nome: proc.nome,
      cor: proc.cor,
      valor_padrao: proc.valor_padrao || ''
    });
    setShowProcedimentoForm(true);
  };

  const handleDeleteProcedimento = async (proc) => {
    const confirmMsg = proc.is_default 
      ? 'Procedimentos padrão serão desativados em vez de excluídos. Continuar?'
      : 'Deseja realmente excluir este procedimento?';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.delete(`${API}/procedimentos/${proc.id}`);
      toast.success(proc.is_default ? 'Procedimento desativado!' : 'Procedimento excluído!');
      loadProcedimentos();
    } catch (error) {
      console.error('Error deleting procedimento:', error);
      toast.error('Erro ao excluir procedimento');
    }
  };

  const cancelProcedimentoForm = () => {
    setShowProcedimentoForm(false);
    setEditingProcedimento(null);
    setNovoProcedimento({ nome: '', cor: '#8B5CF6', valor_padrao: '' });
  };

  const checkSyncStatus = async () => {
    try {
      const response = await axios.get(`${API}/sync/status`);
      setSyncStatus(response.data);
    } catch (error) {
      console.error('Error checking sync status:', error);
    }
  };

  const checkGoogleStatus = async () => {
    try {
      const response = await axios.get(`${API}/google/status`);
      setGoogleStatus(response.data);
      setClientId(response.data.client_id || '');
      
      if (response.data.connected) {
        loadCalendars();
      }
      
      // Show form if no credentials
      if (!response.data.has_credentials) {
        setShowCredentialsForm(true);
      }
    } catch (error) {
      console.error('Error checking Google status:', error);
    }
  };

  const loadCalendars = async () => {
    setLoadingCalendars(true);
    try {
      const response = await axios.get(`${API}/google/calendars`);
      setCalendars(response.data.calendars || []);
      setGoogleStatus(prev => ({
        ...prev,
        selected_calendar_id: response.data.selected_calendar_id || ''
      }));
    } catch (error) {
      console.error('Error loading calendars:', error);
      toast.error('Erro ao carregar calendários');
    } finally {
      setLoadingCalendars(false);
    }
  };

  const handleSaveCredentials = async () => {
    if (!clientId.trim() || !clientSecret.trim()) {
      toast.error('Preencha o Client ID e o Client Secret');
      return;
    }
    
    setSavingCredentials(true);
    try {
      await axios.post(`${API}/google/credentials?client_id=${encodeURIComponent(clientId.trim())}&client_secret=${encodeURIComponent(clientSecret.trim())}`);
      toast.success('Credenciais salvas com sucesso!');
      setGoogleStatus(prev => ({ ...prev, has_credentials: true, client_id: clientId.trim() }));
      setShowCredentialsForm(false);
      setClientSecret(''); // Clear secret from state for security
    } catch (error) {
      console.error('Error saving credentials:', error);
      toast.error('Erro ao salvar credenciais');
    } finally {
      setSavingCredentials(false);
    }
  };

  const handleDeleteCredentials = async () => {
    if (!window.confirm('Deseja realmente remover as credenciais? Isso também desconectará sua conta Google.')) return;
    
    try {
      await axios.delete(`${API}/google/credentials`);
      setGoogleStatus({ connected: false, selected_calendar_id: '', google_email: '', has_credentials: false, client_id: '' });
      setCalendars([]);
      setClientId('');
      setClientSecret('');
      setShowCredentialsForm(true);
      toast.success('Credenciais removidas com sucesso');
    } catch (error) {
      console.error('Error deleting credentials:', error);
      toast.error('Erro ao remover credenciais');
    }
  };

  const handleGoogleConnect = async () => {
    if (!googleStatus.has_credentials) {
      toast.error('Configure suas credenciais do Google primeiro');
      setShowCredentialsForm(true);
      return;
    }
    
    try {
      const origin = window.location.origin;
      const response = await axios.get(`${API}/auth/google/login?origin=${encodeURIComponent(origin)}`);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Error connecting to Google:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao conectar com Google';
      toast.error(errorMsg);
    }
  };

  const handleGoogleDisconnect = async () => {
    if (!window.confirm('Deseja realmente desconectar sua conta Google? As credenciais serão mantidas.')) return;
    
    try {
      await axios.post(`${API}/google/disconnect`);
      setGoogleStatus(prev => ({ ...prev, connected: false, selected_calendar_id: '', google_email: '' }));
      setCalendars([]);
      setShowCalendarSelector(false);
      toast.success('Google desconectado com sucesso');
    } catch (error) {
      console.error('Error disconnecting Google:', error);
      toast.error('Erro ao desconectar Google');
    }
  };

  const handleChangeAccount = async () => {
    // Desconecta a conta atual e inicia novo fluxo OAuth
    try {
      await axios.post(`${API}/google/disconnect`);
      setGoogleStatus(prev => ({ ...prev, connected: false, selected_calendar_id: '', google_email: '' }));
      setCalendars([]);
      
      // Redireciona para nova autenticação
      const origin = window.location.origin;
      const response = await axios.get(`${API}/auth/google/login?origin=${encodeURIComponent(origin)}`);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Error changing account:', error);
      toast.error('Erro ao trocar conta');
    }
  };

  const handleSelectCalendar = async (calendarId) => {
    setSelectingCalendar(calendarId);
    try {
      await axios.post(`${API}/google/calendars/select?calendar_id=${encodeURIComponent(calendarId)}`);
      setGoogleStatus(prev => ({ ...prev, selected_calendar_id: calendarId }));
      setShowCalendarSelector(false);
      toast.success('Calendário selecionado com sucesso!');
    } catch (error) {
      console.error('Error selecting calendar:', error);
      toast.error('Erro ao selecionar calendário');
    } finally {
      setSelectingCalendar(null);
    }
  };

  const handleSyncCalendar = async () => {
    if (!googleStatus.selected_calendar_id) {
      toast.error('Selecione um calendário antes de sincronizar');
      setShowCalendarSelector(true);
      return;
    }
    
    setSyncing(prev => ({ ...prev, calendar: true }));
    try {
      const response = await axios.post(`${API}/sync/calendar`);
      const result = response.data.result;
      toast.success(
        `Sincronização concluída!\n` +
        `• ${result.synced_from_calendar || 0} novos do Calendar\n` +
        `• ${result.synced_to_calendar || 0} enviados ao Calendar\n` +
        `• ${result.updated || 0} atualizados`
      );
    } catch (error) {
      console.error('Error syncing calendar:', error);
      toast.error('Erro ao sincronizar calendário');
    } finally {
      setSyncing(prev => ({ ...prev, calendar: false }));
    }
  };

  const handleSyncSheets = async () => {
    setSyncing(prev => ({ ...prev, sheets: true }));
    try {
      const response = await axios.post(`${API}/sync/sheets`);
      toast.success(`Planilha sincronizada! ${response.data.result?.synced || 0} pacientes`);
    } catch (error) {
      console.error('Error syncing sheets:', error);
      toast.error('Erro ao sincronizar planilha');
    } finally {
      setSyncing(prev => ({ ...prev, sheets: false }));
    }
  };

  const getSelectedCalendarName = () => {
    if (!googleStatus.selected_calendar_id) return 'Nenhum selecionado';
    const cal = calendars.find(c => c.id === googleStatus.selected_calendar_id);
    return cal?.summary || googleStatus.selected_calendar_id;
  };

  // API Key functions
  const loadApiKey = async () => {
    setLoadingApiKey(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/config/api-key`, { headers: { Authorization: `Bearer ${token}` } });
      setApiKey(res.data.api_key || null);
      setApiKeyCreatedAt(res.data.created_at || null);
    } catch {
      // Not master or no key yet
    } finally {
      setLoadingApiKey(false);
    }
  };

  const handleGenerateApiKey = async () => {
    setGeneratingApiKey(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/config/api-key/generate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setApiKey(res.data.api_key);
      setApiKeyCreatedAt(new Date().toISOString());
      setShowApiKey(true);
      toast.success('API Key gerada com sucesso!');
    } catch {
      toast.error('Erro ao gerar API Key');
    } finally {
      setGeneratingApiKey(false);
    }
  };

  const handleCopyApiKey = () => {
    if (apiKey) {
      navigator.clipboard.writeText(apiKey);
      setCopiedApiKey(true);
      toast.success('API Key copiada!');
      setTimeout(() => setCopiedApiKey(false), 2000);
    }
  };

  const formatApiKeyDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return dateStr; }
  };

  const getCurlRetornos = () => {
    const key = showApiKey ? apiKey : (apiKey || 'SUA_API_KEY');
    return `curl -X GET "${BACKEND_URL}/api/public/retornos-disponiveis?telefone=11999999999" \\\n  -H "X-API-Key: ${key}"`;
  };

  const handleCopyCurl = () => {
    const curl = `curl -X GET "${BACKEND_URL}/api/public/retornos-disponiveis?telefone=11999999999" -H "X-API-Key: ${apiKey}"`;
    navigator.clipboard.writeText(curl);
    setCopiedCurl(true);
    toast.success('cURL copiado! Cole no "Import cURL" do N8N');
    setTimeout(() => setCopiedCurl(false), 2000);
  };

  return (
    <Layout user={user} onLogout={onLogout} showConfig={showConfig}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Configurações</h1>
          <p className="text-gray-600 mt-1">Gerencie as integrações e sincronizações do sistema</p>
        </div>

        {/* Google Integration Card */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center">
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Google Integration</h2>
                <p className="text-sm text-gray-500">Conecte com Google Calendar e Sheets</p>
              </div>
            </div>
            
            {googleStatus.connected && (
              <div className="flex items-center space-x-2">
                <span className="flex items-center text-green-600 text-sm font-medium">
                  <Check className="w-4 h-4 mr-1" />
                  Conectado
                </span>
              </div>
            )}
          </div>

          {/* Step 1: Credentials */}
          <div className="space-y-6">
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">1</div>
                  <div>
                    <h3 className="font-medium text-gray-900">Credenciais do Google Cloud</h3>
                    <p className="text-sm text-gray-500">
                      {googleStatus.has_credentials 
                        ? `Client ID: ${googleStatus.client_id?.substring(0, 20)}...` 
                        : 'Configure suas credenciais OAuth'}
                    </p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  {googleStatus.has_credentials ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowCredentialsForm(!showCredentialsForm)}
                      >
                        {showCredentialsForm ? 'Fechar' : 'Editar'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDeleteCredentials}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        Remover
                      </Button>
                    </>
                  ) : (
                    <span className="text-amber-700 text-sm font-medium">⚠️ Pendente</span>
                  )}
                </div>
              </div>

              {showCredentialsForm && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
                    <p className="font-medium mb-2">Como obter as credenciais:</p>
                    <ol className="list-decimal list-inside space-y-1 text-xs">
                      <li>Acesse o <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="underline font-medium">Google Cloud Console</a></li>
                      <li>Crie um projeto ou selecione um existente</li>
                      <li>Vá em <strong>APIs e Serviços → Credenciais</strong></li>
                      <li>Clique em <strong>Criar Credenciais → ID do Cliente OAuth</strong></li>
                      <li>Tipo: <strong>Aplicativo da Web</strong></li>
                      <li>Em <strong>URIs de redirecionamento autorizados</strong>, adicione:</li>
                    </ol>
                    <code className="block bg-blue-100 p-2 rounded mt-2 text-xs break-all">
                      {window.location.origin}/api/auth/google/callback
                    </code>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client ID <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value)}
                      placeholder="Seu Google Client ID"
                      className="font-mono text-sm"
                      data-testid="input-client-id"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client Secret <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Input
                        type={showSecret ? 'text' : 'password'}
                        value={clientSecret}
                        onChange={(e) => setClientSecret(e.target.value)}
                        placeholder="Seu Google Client Secret"
                        className="font-mono text-sm pr-10"
                        data-testid="input-client-secret"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSecret(!showSecret)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <Button
                    onClick={handleSaveCredentials}
                    disabled={savingCredentials}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="btn-save-credentials"
                  >
                    <Key className="w-4 h-4 mr-2" />
                    {savingCredentials ? 'Salvando...' : 'Salvar Credenciais'}
                  </Button>
                </div>
              )}
            </div>

            {/* Step 2: Connect */}
            <div className={`border rounded-lg p-4 ${!googleStatus.has_credentials ? 'opacity-50' : ''}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">2</div>
                  <div>
                    <h3 className="font-medium text-gray-900">Conectar Conta Google</h3>
                    <p className="text-sm text-gray-500">
                      {googleStatus.connected 
                        ? googleStatus.google_email || 'Conectado' 
                        : 'Autorize o acesso à sua conta'}
                    </p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  {googleStatus.connected ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleChangeAccount}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        data-testid="btn-change-account"
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        Trocar Conta
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleGoogleDisconnect}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid="btn-google-disconnect"
                      >
                        <Unlink className="w-4 h-4 mr-1" />
                        Desconectar
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={handleGoogleConnect}
                      disabled={!googleStatus.has_credentials}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="btn-google-connect"
                    >
                      <Link2 className="w-4 h-4 mr-2" />
                      Conectar com Google
                    </Button>
                  )}
                </div>
              </div>

              {googleStatus.connected && googleStatus.google_email && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm text-green-800 font-medium">Conta conectada:</p>
                    <p className="text-green-900 font-semibold">{googleStatus.google_email}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Step 3: Calendar Selection - Only show if connected */}
            {googleStatus.connected && (
              <>
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">3</div>
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-5 h-5 text-blue-600" />
                        <div>
                          <h3 className="font-medium text-gray-900">Calendário para Sincronização</h3>
                          <p className="text-sm text-gray-500">
                            Selecionado: <span className="font-medium">{getSelectedCalendarName()}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowCalendarSelector(!showCalendarSelector)}
                        data-testid="btn-select-calendar"
                      >
                        {showCalendarSelector ? 'Fechar' : 'Selecionar'}
                      </Button>
                      <Button
                        onClick={handleSyncCalendar}
                        disabled={syncing.calendar || !googleStatus.selected_calendar_id}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid="btn-sync-calendar"
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${syncing.calendar ? 'animate-spin' : ''}`} />
                        {syncing.calendar ? 'Sincronizando...' : 'Sincronizar'}
                      </Button>
                    </div>
                  </div>

                  {/* Calendar Selector */}
                  {showCalendarSelector && (
                    <div className="bg-gray-50 rounded-lg p-4 mt-4">
                      <h4 className="font-medium text-gray-900 mb-3">Selecione o calendário:</h4>
                      {loadingCalendars ? (
                        <div className="flex items-center justify-center py-4">
                          <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
                          <span className="ml-2 text-gray-600">Carregando calendários...</span>
                        </div>
                      ) : calendars.length > 0 ? (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {calendars.map((cal) => (
                            <button
                              key={cal.id}
                              onClick={() => handleSelectCalendar(cal.id)}
                              disabled={selectingCalendar === cal.id}
                              className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                                googleStatus.selected_calendar_id === cal.id
                                  ? 'bg-blue-50 border-blue-300'
                                  : 'bg-white border-gray-200 hover:bg-gray-50'
                              }`}
                              data-testid={`calendar-option-${cal.id}`}
                            >
                              <div className="flex items-center space-x-3">
                                <div
                                  className="w-4 h-4 rounded"
                                  style={{ backgroundColor: cal.backgroundColor }}
                                ></div>
                                <div className="text-left">
                                  <p className="font-medium text-gray-900">{cal.summary}</p>
                                  {cal.primary && (
                                    <span className="text-xs text-blue-600 font-medium">Calendário principal</span>
                                  )}
                                </div>
                              </div>
                              {googleStatus.selected_calendar_id === cal.id ? (
                                <Check className="w-5 h-5 text-blue-600" />
                              ) : selectingCalendar === cal.id ? (
                                <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
                              ) : null}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-4">Nenhum calendário encontrado</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Google Sheets */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Sheet className="w-5 h-5 text-green-600" />
                      <div>
                        <h3 className="font-medium text-gray-900">Google Sheets</h3>
                        <p className="text-sm text-gray-500">Sincronize a lista de pacientes</p>
                      </div>
                    </div>
                    <Button
                      onClick={handleSyncSheets}
                      disabled={syncing.sheets}
                      variant="outline"
                      className="border-green-600 text-green-600 hover:bg-green-50"
                      data-testid="btn-sync-sheets"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${syncing.sheets ? 'animate-spin' : ''}`} />
                      {syncing.sheets ? 'Sincronizando...' : 'Sincronizar'}
                    </Button>
                  </div>
                </div>

                {/* Auto Sync Status */}
                <div className={`p-4 rounded-lg border-2 ${syncStatus.auto_sync_enabled ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${syncStatus.auto_sync_enabled ? 'bg-green-100' : 'bg-gray-200'}`}>
                        <Zap className={`w-5 h-5 ${syncStatus.auto_sync_enabled ? 'text-green-600' : 'text-gray-500'}`} />
                      </div>
                      <div>
                        <h3 className={`font-medium ${syncStatus.auto_sync_enabled ? 'text-green-900' : 'text-gray-700'}`}>
                          Sincronização Automática
                        </h3>
                        <p className={`text-sm ${syncStatus.auto_sync_enabled ? 'text-green-700' : 'text-gray-500'}`}>
                          {syncStatus.auto_sync_enabled 
                            ? `Ativa - Atualizando a cada ${syncStatus.sync_interval_seconds} segundos`
                            : 'Inativa'}
                        </p>
                      </div>
                    </div>
                    {syncStatus.auto_sync_enabled && (
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
                        <span className="text-green-700 text-sm font-medium">Online</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sync Info */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Sincronização Bilateral</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Consultas criadas no app serão enviadas ao Google Calendar</li>
                    <li>• Eventos do Google Calendar serão importados para o app</li>
                    <li>• Alterações e cancelamentos são sincronizados em ambas as direções</li>
                    <li>• Formato do evento: "Nome do Paciente - Procedimento"</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </Card>

        {/* Procedimentos Card */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Procedimentos</h2>
                <p className="text-sm text-gray-500">Configure os tipos de procedimentos disponíveis</p>
              </div>
            </div>
            <Button
              onClick={() => {
                setEditingProcedimento(null);
                setNovoProcedimento({ nome: '', cor: '#8B5CF6', valor_padrao: '' });
                setShowProcedimentoForm(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="btn-novo-procedimento"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Procedimento
            </Button>
          </div>

          {/* Form de novo/editar procedimento */}
          {showProcedimentoForm && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 border">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-gray-900">
                  {editingProcedimento ? 'Editar Procedimento' : 'Novo Procedimento'}
                </h3>
                <button onClick={cancelProcedimentoForm} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Procedimento <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={novoProcedimento.nome}
                    onChange={(e) => setNovoProcedimento(prev => ({ ...prev, nome: e.target.value }))}
                    placeholder="Ex: Ultrassom"
                    data-testid="input-proc-nome"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cor no Calendário
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {CORES_DISPONIVEIS.map((c) => (
                      <button
                        key={c.cor}
                        type="button"
                        onClick={() => setNovoProcedimento(prev => ({ ...prev, cor: c.cor }))}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          novoProcedimento.cor === c.cor 
                            ? 'border-gray-900 scale-110' 
                            : 'border-transparent hover:scale-105'
                        }`}
                        style={{ backgroundColor: c.cor }}
                        title={c.nome}
                        data-testid={`cor-${c.nome.toLowerCase()}`}
                      />
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor Padrão (R$)
                  </label>
                  <Input
                    type="number"
                    value={novoProcedimento.valor_padrao}
                    onChange={(e) => setNovoProcedimento(prev => ({ ...prev, valor_padrao: e.target.value }))}
                    placeholder="0,00"
                    data-testid="input-proc-valor"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-2 mt-4">
                <Button variant="outline" onClick={cancelProcedimentoForm}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleSaveProcedimento}
                  disabled={savingProcedimento}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="btn-salvar-procedimento"
                >
                  {savingProcedimento ? 'Salvando...' : (editingProcedimento ? 'Atualizar' : 'Criar')}
                </Button>
              </div>
            </div>
          )}

          {/* Lista de procedimentos */}
          {loadingProcedimentos ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Carregando procedimentos...</span>
            </div>
          ) : procedimentos.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Nenhum procedimento cadastrado
            </div>
          ) : (
            <div className="space-y-2">
              {procedimentos.map((proc) => (
                <div
                  key={proc.id}
                  className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-gray-50"
                  data-testid={`procedimento-${proc.id}`}
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-6 h-6 rounded-full"
                      style={{ backgroundColor: proc.cor }}
                    />
                    <div>
                      <span className="font-medium text-gray-900">{proc.nome}</span>
                      {proc.valor_padrao && (
                        <span className="ml-2 text-sm text-gray-500">
                          R$ {proc.valor_padrao.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      )}
                      {proc.is_default && (
                        <span className="ml-2 px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">
                          Padrão
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditProcedimento(proc)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      title="Editar"
                      data-testid={`btn-edit-proc-${proc.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteProcedimento(proc)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                      title={proc.is_default ? 'Desativar' : 'Excluir'}
                      data-testid={`btn-delete-proc-${proc.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* API Key para Integrações */}
        <Card className="p-6" data-testid="api-key-section">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-amber-50 border-2 border-amber-200 flex items-center justify-center">
              <Shield className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">API Key para Integrações</h2>
              <p className="text-sm text-gray-500">Chave única da sua clínica para conectar sistemas externos com segurança</p>
            </div>
          </div>

          {/* API Key Display */}
          {loadingApiKey ? (
            <div className="flex items-center justify-center py-6">
              <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : apiKey ? (
            <>
              <div className="flex items-center gap-2 mb-1">
                <div className="flex-1 relative">
                  <Input
                    type={showApiKey ? 'text' : 'password'}
                    value={apiKey}
                    readOnly
                    className="pr-20 font-mono text-sm"
                    data-testid="api-key-input"
                  />
                  <div className="absolute right-1 top-1/2 -translate-y-1/2 flex gap-1">
                    <button
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                      title={showApiKey ? 'Ocultar' : 'Mostrar'}
                      data-testid="api-key-toggle-visibility"
                    >
                      {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <button
                  onClick={handleCopyApiKey}
                  className={`px-3 py-2 rounded-lg border text-sm flex items-center gap-1.5 transition-colors ${copiedApiKey ? 'border-green-300 text-green-600 bg-green-50' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                  data-testid="api-key-copy"
                >
                  {copiedApiKey ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  Copiar
                </button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleGenerateApiKey}
                  disabled={generatingApiKey}
                  data-testid="api-key-regenerate"
                >
                  <RefreshCw className={`w-4 h-4 mr-1.5 ${generatingApiKey ? 'animate-spin' : ''}`} />
                  Gerar Nova
                </Button>
              </div>
              {apiKeyCreatedAt && (
                <p className="text-xs text-gray-400 mb-0">Gerada em: {formatApiKeyDate(apiKeyCreatedAt)}</p>
              )}

              {/* Endpoints Section */}
              <div className="border-t border-gray-100 mt-5 pt-5">
                <h3 className="text-base font-bold text-gray-900 mb-1">Endpoints Disponíveis</h3>
                <p className="text-xs text-gray-400 mb-4">Clique em um endpoint para ver como configurar no N8N</p>

                <div className="space-y-3">
                  {/* Endpoint: Retornos Disponíveis */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid="endpoint-retornos">
                    <button
                      onClick={() => setExpandedEndpoint(expandedEndpoint === 'retornos' ? null : 'retornos')}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="px-2 py-0.5 rounded text-[11px] font-bold font-mono bg-green-100 text-green-700 tracking-wide">GET</span>
                        <div className="text-left">
                          <p className="text-sm font-semibold text-gray-900">Retornos Disponíveis</p>
                          <p className="text-[11px] text-gray-500">Verifica se o paciente tem retorno disponível</p>
                        </div>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedEndpoint === 'retornos' ? 'rotate-180' : ''}`} />
                    </button>

                    {expandedEndpoint === 'retornos' && (
                      <div className="p-4 border-t border-gray-100 space-y-5">
                        {/* cURL Section */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Rocket className="w-3.5 h-3.5 text-blue-500" />
                            <span className="text-xs font-semibold text-gray-600">Import cURL — cole no N8N para configurar automaticamente</span>
                          </div>
                          <div className="relative bg-gray-900 rounded-lg p-4 pr-24">
                            <button
                              onClick={handleCopyCurl}
                              className={`absolute top-2.5 right-2.5 px-2.5 py-1.5 rounded-md text-[11px] flex items-center gap-1.5 transition-colors ${copiedCurl ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'}`}
                              data-testid="curl-copy-btn"
                            >
                              {copiedCurl ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                              {copiedCurl ? 'Copiado!' : 'Copiar cURL'}
                            </button>
                            <pre className="text-xs font-mono text-gray-300 whitespace-pre-wrap break-all leading-relaxed">
                              <span className="text-amber-400 font-bold">curl</span> <span className="text-gray-500">-X GET</span> <span className="text-green-400">&quot;{BACKEND_URL}/api/public/retornos-disponiveis?telefone=11999999999&quot;</span> \{'\n'}  <span className="text-gray-500">-H</span> <span className="text-amber-400">&quot;X-API-Key:</span> <span className="text-cyan-400">{showApiKey ? apiKey : apiKey?.substring(0, 15) + '...'}&quot;</span>
                            </pre>
                          </div>
                          <div className="flex items-start gap-2 mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                            <span className="text-amber-600 text-xs mt-px">&#9888;</span>
                            <p className="text-[11px] text-amber-800">
                              No N8N, clique em <strong>&quot;Import cURL&quot;</strong> e cole o comando acima. Depois troque <strong>11999999999</strong> pela variável do seu workflow: <code className="bg-white px-1 rounded text-amber-900 border border-amber-300">{'{{ $json.telefone }}'}</code>
                            </p>
                          </div>
                        </div>

                        {/* N8N Fields Reference */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Settings className="w-3.5 h-3.5 text-gray-500" />
                            <span className="text-xs font-semibold text-gray-600">Referência dos campos no N8N (após importar o cURL)</span>
                          </div>
                          <div className="bg-amber-50/60 border border-dashed border-amber-300 rounded-xl p-4">
                            <p className="text-[11px] font-bold text-amber-800 mb-3 flex items-center gap-1.5">
                              <Settings className="w-3 h-3" /> Campos do HTTP Request
                            </p>
                            <div className="space-y-0 divide-y divide-dashed divide-amber-200">
                              {[
                                { label: 'Method', value: 'GET' },
                                { label: 'URL', value: `${BACKEND_URL}/api/public/retornos-disponiveis`, mono: true },
                                { label: 'Authentication', value: 'None' },
                                { label: 'Send Query Parameters', value: 'Ativado', toggle: 'on' },
                                { label: 'Name', value: 'telefone', indent: true },
                                { label: 'Value', value: '{{ $json.telefone }}', indent: true },
                                { label: 'Send Headers', value: 'Ativado', toggle: 'on' },
                                { label: 'Name', value: 'X-API-Key', indent: true },
                                { label: 'Value', value: showApiKey ? apiKey : apiKey?.substring(0, 20) + '...', indent: true },
                                { label: 'Send Body', value: 'Desativado', toggle: 'off' },
                              ].map((field, idx) => (
                                <div key={idx} className="flex items-center gap-3 py-2">
                                  <span className={`text-[12px] font-semibold text-amber-900 w-[200px] flex-shrink-0 ${field.indent ? 'pl-6 font-normal' : ''}`}>
                                    {field.label}
                                  </span>
                                  <span className={`text-[12px] flex-1 px-2.5 py-1 rounded border ${
                                    field.toggle === 'on' ? 'bg-green-100 border-green-300 text-green-700 font-semibold' :
                                    field.toggle === 'off' ? 'bg-red-50 border-red-200 text-red-600 font-semibold' :
                                    'bg-white border-amber-300 text-amber-950 font-mono'
                                  }`}>
                                    {field.value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Info box */}
                        <div className="flex items-start gap-2.5 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl">
                          <span className="text-blue-500 text-sm mt-px">&#128161;</span>
                          <div className="text-xs text-blue-800 leading-relaxed">
                            <p><strong>Parâmetros de busca:</strong> <code className="bg-white px-1 rounded border border-blue-200">telefone</code>, <code className="bg-white px-1 rounded border border-blue-200">cpf</code> ou <code className="bg-white px-1 rounded border border-blue-200">email</code>. O sistema busca nessa ordem automaticamente.</p>
                            <p className="mt-1"><strong>No próximo node:</strong> Use <code className="bg-white px-1 rounded border border-blue-200">{'{{ $json.tem_retorno }}'}</code> — se <code>true</code>, não cobra Sinal. Se <code>false</code>, cobra R$ 100.</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Future: Sinal de Pagamento */}
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border border-dashed border-gray-200 rounded-xl">
                    <div className="flex items-center gap-3">
                      <span className="px-2 py-0.5 rounded text-[11px] font-bold font-mono bg-gray-100 text-gray-400 tracking-wide">POST</span>
                      <div>
                        <p className="text-sm font-medium text-gray-400">Registrar Sinal de Pagamento</p>
                        <p className="text-[11px] text-gray-300">N8N registra comprovante PIX do paciente</p>
                      </div>
                    </div>
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-400 border border-gray-200">Em breve</span>
                  </div>

                  {/* Future: Agendar Retorno */}
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border border-dashed border-gray-200 rounded-xl">
                    <div className="flex items-center gap-3">
                      <span className="px-2 py-0.5 rounded text-[11px] font-bold font-mono bg-gray-100 text-gray-400 tracking-wide">POST</span>
                      <div>
                        <p className="text-sm font-medium text-gray-400">Agendar Retorno</p>
                        <p className="text-[11px] text-gray-300">N8N agenda retorno diretamente no sistema</p>
                      </div>
                    </div>
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-400 border border-gray-200">Em breve</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-6">
              <p className="text-sm text-gray-500 mb-3">Nenhuma API Key gerada ainda.</p>
              <Button
                onClick={handleGenerateApiKey}
                disabled={generatingApiKey}
                data-testid="api-key-generate"
              >
                <Key className={`w-4 h-4 mr-2 ${generatingApiKey ? 'animate-spin' : ''}`} />
                {generatingApiKey ? 'Gerando...' : 'Gerar API Key'}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}

export default ConfiguracoesPage;
